import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, Note, FloppyDisk, Calendar, CaretUp, CaretDown, Repeat } from "@phosphor-icons/react";
import MoveBlockDialog from "./MoveBlockDialog";
import { toast } from "sonner";
import { BLOCK_TYPES, describeBlock } from "./blockMeta";
import BlockForm from "./BlockForm";
import BlockCompletionDialog from "./BlockCompletionDialog";
import MovementForm from "./MovementForm";
import MovementCard from "./MovementCard";
import PriorAttempts from "./PriorAttempts";
import BlockTimer from "@/components/app/BlockTimer";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { splitSeconds, DEFAULT_INTERVAL_GOAL } from "@/lib/intervals";
import { fromCanonicalMeters } from "@/lib/units";

function editMinSecFields(sc) {
  const work = splitSeconds(sc.interval_seconds);
  const rest = splitSeconds(sc.rest_seconds);
  const tw = splitSeconds(sc.work_seconds);
  const target = splitSeconds(sc.target_time_seconds);
  return {
    emom_work_min: work.min, emom_work_sec: work.sec,
    rest_min: rest.min, rest_sec: rest.sec,
    tabata_work_min: tw.min, tabata_work_sec: tw.sec,
    tabata_rest_min: rest.min, tabata_rest_sec: rest.sec,
    interval_goal: sc.interval_goal || DEFAULT_INTERVAL_GOAL,
    target_time_min: target.min, target_time_sec: target.sec,
    target_calories: sc.target_calories ?? "",
    target_distance: fromCanonicalMeters(sc.target_distance_meters, sc.target_distance_unit || "m") ?? "",
    target_distance_unit: sc.target_distance_unit || "m",
  };
}

export default function BlockCard({ blk, program, dayIdx, canEditCore, athleteId, userRole, priorAttempts,
                    blockIndex = 0, totalBlocks = 1, onReorderBlock,
                    onBlockEdited, onBlockDeleted, onMovementAdded, onMovementEdited, onMovementDeleted, onMovementsReordered, onMoved }) {
  const [editOpen, setEditOpen] = useState(false);
  const [addMvOpen, setAddMvOpen] = useState(false);
  const meta = BLOCK_TYPES[blk.type] || BLOCK_TYPES.strength;
  const Icon = meta.icon;
  const desc = describeBlock(blk);
  const isStrength = blk.type === "strength";
  // Circuit / Warm Up / Mobility use the same UX as Strength — per-set log,
  // no scheme-driven timer, no aggregate "block completion" step.
  const isStrengthLike = isStrength || blk.type === "circuit" || blk.type === "warmup" || blk.type === "mobility";

  const saveBlock = async (patch) => {
    try {
      const { data } = await api.patch(`/programs/${program.id}/days/${dayIdx}/blocks/${blk.id}`, patch);
      toast.success("Section updated");
      setEditOpen(false);
      onBlockEdited(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeType = async (newType) => {
    if (newType === blk.type) return;
    try {
      // Wipe the old scheme_config so a Strength→AMRAP switch doesn't drag
      // orphan rounds/caps along, and reset order-metadata (server ignores).
      const { data } = await api.patch(
        `/programs/${program.id}/days/${dayIdx}/blocks/${blk.id}`,
        { type: newType, scheme_config: {} },
      );
      toast.success(`Converted to ${BLOCK_TYPES[newType]?.label || newType}`);
      onBlockEdited(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delBlock = async () => {
    if (!window.confirm(`Delete this ${meta.label} section?`)) return;
    try {
      await api.delete(`/programs/${program.id}/days/${dayIdx}/blocks/${blk.id}`);
      toast.success("Section removed");
      onBlockDeleted(blk.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const addMvInBlock = async (mv) => {
    try {
      const { data } = await api.post(`/programs/${program.id}/days/${dayIdx}/blocks/${blk.id}/movements`, mv);
      onMovementAdded(blk.id, data);
      setAddMvOpen(false);
      toast.success("Movement added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const moveMovement = async (movementId, delta) => {
    const current = blk.movements || [];
    const idx = current.findIndex((m) => m.id === movementId);
    if (idx < 0) return;
    const next = idx + delta;
    if (next < 0 || next >= current.length) return;
    // Optimistic UI swap.
    const reordered = current.slice();
    [reordered[idx], reordered[next]] = [reordered[next], reordered[idx]];
    onMovementsReordered?.(blk.id, reordered);
    try {
      await api.patch(
        `/programs/${program.id}/days/${dayIdx}/blocks/${blk.id}/reorder-movements`,
        { movement_ids: reordered.map((m) => m.id) },
      );
    } catch (e) {
      // Roll back on error.
      onMovementsReordered?.(blk.id, current);
      toast.error(formatApiError(e));
    }
  };

  // Toggle a superset link on the given movement:
  //   • If it already has a group → clear it (leave the block).
  //   • Else → adopt the previous movement's group_id (creating one on the
  //     previous movement if needed). Requires a movement above to link to.
  const toggleSuperset = async (movementId) => {
    const movements = blk.movements || [];
    const idx = movements.findIndex((m) => m.id === movementId);
    if (idx < 0) return;
    const target = movements[idx];
    if (target.superset_group_id) {
      // Unlink target. If the previous member is also in this group and
      // the target is the last one, we also strip the previous member so a
      // dangling one-item group doesn't linger.
      const group = target.superset_group_id;
      const groupIds = movements.filter((m) => m.superset_group_id === group).map((m) => m.id);
      try {
        await api.patch(
          `/programs/${program.id}/days/${dayIdx}/blocks/${blk.id}/movements/${movementId}`,
          { superset_group_id: null },
        );
        onMovementEdited?.(blk.id, { ...target, superset_group_id: null });
        // Also unlink the lone survivor if only one member remains.
        if (groupIds.length === 2) {
          const otherId = groupIds.find((id) => id !== movementId);
          const other = movements.find((m) => m.id === otherId);
          await api.patch(
            `/programs/${program.id}/days/${dayIdx}/blocks/${blk.id}/movements/${otherId}`,
            { superset_group_id: null },
          );
          onMovementEdited?.(blk.id, { ...other, superset_group_id: null });
        }
        toast.success("Removed from superset");
      } catch (e) {
        toast.error(formatApiError(e));
      }
      return;
    }
    // Link to the previous movement.
    if (idx === 0) {
      toast.error("Move this movement below another one first, then link.");
      return;
    }
    const prev = movements[idx - 1];
    const groupId = prev.superset_group_id || (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2));
    try {
      if (!prev.superset_group_id) {
        await api.patch(
          `/programs/${program.id}/days/${dayIdx}/blocks/${blk.id}/movements/${prev.id}`,
          { superset_group_id: groupId },
        );
        onMovementEdited?.(blk.id, { ...prev, superset_group_id: groupId });
      }
      await api.patch(
        `/programs/${program.id}/days/${dayIdx}/blocks/${blk.id}/movements/${movementId}`,
        { superset_group_id: groupId },
      );
      onMovementEdited?.(blk.id, { ...target, superset_group_id: groupId });
      toast.success("Linked as superset");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveAsTemplate = async () => {    const movements = blk.movements || [];
    if (movements.length === 0 && !window.confirm("This section has no movements yet. Save anyway?")) return;
    const defaultTitle = blk.title || `${meta.label}${movements.length ? ` · ${movements.length} movement${movements.length === 1 ? "" : "s"}` : ""}`;
    const title = window.prompt("Save this workout as a reusable template — name it:", defaultTitle);
    if (!title || !title.trim()) return;
    try {
      // Strip the program-specific IDs; backend will generate fresh ones.
      const cleanMovements = movements.map(({ id, ...rest }) => rest);
      const body = {
        title: title.trim(),
        type: blk.type,
        scheme_config: blk.scheme_config || {},
        notes: blk.notes || null,
        movements: cleanMovements,
        source_slug: null,
      };
      await api.post("/block-templates", body);
      toast.success("Saved to your template library");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="hs-card p-4 sm:p-5 animate-fade-up" data-testid={`block-${blk.id}`}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2.5 min-w-0 w-full">
          <div className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${meta.tint}`}>
            <Icon size={16} weight="bold" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-heading font-semibold text-base text-ink break-words">
              {blk.title || meta.label}
            </div>
            {desc && <div className="text-[11px] uppercase tracking-[0.16em] text-ink-3 mt-0.5 break-words font-data">{desc}</div>}
          </div>
        </div>
        {canEditCore && (
          <div className="flex gap-1 shrink-0 flex-wrap">
            {totalBlocks > 1 && onReorderBlock && (
              <div className="flex flex-col items-center mr-0.5">
                <button
                  type="button"
                  onClick={() => onReorderBlock(-1)}
                  disabled={blockIndex === 0}
                  className="h-4 w-6 grid place-items-center rounded-t-md text-ink-3 hover:text-moss hover:bg-sage disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  title="Move section up"
                  data-testid={`move-block-up-${blk.id}`}
                >
                  <CaretUp size={12} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => onReorderBlock(1)}
                  disabled={blockIndex === totalBlocks - 1}
                  className="h-4 w-6 grid place-items-center rounded-b-md text-ink-3 hover:text-moss hover:bg-sage disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  title="Move section down"
                  data-testid={`move-block-down-${blk.id}`}
                >
                  <CaretDown size={12} weight="bold" />
                </button>
              </div>
            )}
            <MoveBlockDialog
              programId={program.id}
              dayIdx={dayIdx}
              blockId={blk.id}
              blockTitle={blk.title || meta.label}
              athleteId={program.athlete_id || athleteId}
              onDone={() => onMoved && onMoved()}
              trigger={
                <Button
                  size="icon"
                  variant="ghost"
                  className="rounded-lg h-8 w-8 hover:bg-sage hover:text-moss"
                  title="Move or copy to another date / athlete"
                  data-testid={`move-block-${blk.id}`}
                >
                  <Calendar size={15} />
                </Button>
              }
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="rounded-lg h-8 w-8 hover:bg-sage hover:text-moss"
                  title="Convert this section to a different type"
                  data-testid={`convert-block-${blk.id}`}
                >
                  <Repeat size={15} />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-2" data-testid={`convert-block-menu-${blk.id}`}>
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-3 px-2 pb-1">
                  Convert to…
                </div>
                {Object.entries(BLOCK_TYPES).map(([key, m]) => {
                  const MetaIcon = m.icon;
                  const active = key === blk.type;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => !active && changeType(key)}
                      disabled={active}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm transition-colors ${
                        active ? "bg-sage/60 text-moss font-semibold cursor-default" : "hover:bg-muted2 text-ink"
                      }`}
                      data-testid={`convert-block-option-${key}-${blk.id}`}
                    >
                      <MetaIcon size={14} className="shrink-0" />
                      <span className="flex-1 truncate">{m.label}</span>
                      {active && <span className="text-[10px] text-ink-3 uppercase tracking-wider">current</span>}
                    </button>
                  );
                })}
              </PopoverContent>
            </Popover>
            <Button
              size="icon"
              variant="ghost"
              onClick={saveAsTemplate}
              className="rounded-lg h-8 w-8 hover:bg-sage hover:text-moss"
              title="Save as a reusable template"
              data-testid={`save-template-${blk.id}`}
            >
              <FloppyDisk size={15} />
            </Button>
            {!isStrengthLike && (
              <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogTrigger asChild>
                  <Button size="icon" variant="ghost" className="rounded-lg h-8 w-8 hover:bg-muted2" data-testid={`edit-block-${blk.id}`}><PencilSimple size={15} /></Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Edit {meta.label}</DialogTitle>
                    <DialogDescription>Adjust rounds, time caps, and scheme.</DialogDescription>
                  </DialogHeader>
                  <BlockForm
                    type={blk.type}
                    isEdit
                    initial={{
                      title: blk.title || "",
                      notes: blk.notes || "",
                      rounds: blk.scheme_config?.rounds ?? "",
                      time_cap_minutes: blk.scheme_config?.time_cap_seconds != null ? +(blk.scheme_config.time_cap_seconds / 60).toFixed(2) : "",
                      interval_minutes: blk.scheme_config?.interval_seconds != null ? +(blk.scheme_config.interval_seconds / 60).toFixed(2) : "",
                      work_minutes: blk.scheme_config?.work_seconds != null ? +(blk.scheme_config.work_seconds / 60).toFixed(2) : "",
                      rest_minutes: blk.scheme_config?.rest_seconds != null ? +(blk.scheme_config.rest_seconds / 60).toFixed(2) : "",
                      rep_scheme: (blk.scheme_config?.rep_scheme || []).join(", "),
                      ...editMinSecFields(blk.scheme_config || {}),
                    }}
                    onSave={saveBlock}
                    onCancel={() => setEditOpen(false)}
                  />
                </DialogContent>
              </Dialog>
            )}
            <Button size="icon" variant="ghost" onClick={delBlock} className="rounded-lg h-8 w-8 text-error hover:bg-red-50 hover:text-error" data-testid={`delete-block-${blk.id}`}>
              <Trash size={15} />
            </Button>
          </div>
        )}
      </div>

      {blk.notes && (
        <div className="mt-3 flex items-start gap-2 text-sm text-ink-2">
          <Note size={14} className="mt-1 shrink-0" />
          <p>{blk.notes}</p>
        </div>
      )}

      {!isStrengthLike && priorAttempts && (
        <PriorAttempts
          record={priorAttempts}
          blockType={blk.type}
          testId={`prior-attempts-${blk.id}`}
        />
      )}

      <div className="mt-4 space-y-3">
        {(blk.movements || []).length === 0 ? (
          <div className="py-6 text-center text-sm text-ink-3 border border-dashed border-line rounded-xl">
            {canEditCore ? "No movements in this section yet." : "No movements."}
          </div>
        ) : (() => {
          // Number supersets in order of first appearance: the earliest
          // movement in a group determines its "Superset N" label. Athletes
          // and coaches will both see the same numbering.
          const supersetNumbers = {};
          let nextNumber = 1;
          for (const m of blk.movements) {
            const g = m.superset_group_id;
            if (g && !(g in supersetNumbers)) supersetNumbers[g] = nextNumber++;
          }
          return blk.movements.map((mv, idx) => {
            const prev = idx > 0 ? blk.movements[idx - 1] : null;
            const isSupersetHead = !!mv.superset_group_id && (!prev || prev.superset_group_id !== mv.superset_group_id);
            return (
              <MovementCard
                key={mv.id}
                mv={mv}
                program={program}
                dayIdx={dayIdx}
                blockId={blk.id}
                blockType={blk.type}
                readOnly={false}
                canEditCore={canEditCore}
                athleteId={athleteId}
                onEdited={(m) => onMovementEdited(blk.id, m)}
                onDeleted={(id) => onMovementDeleted(blk.id, id)}
                hideLogButton={!isStrengthLike}
                onMoveUp={(id) => moveMovement(id, -1)}
                onMoveDown={(id) => moveMovement(id, +1)}
                canMoveUp={idx > 0}
                canMoveDown={idx < blk.movements.length - 1}
                isSupersetMember={!!mv.superset_group_id}
                isSupersetHead={isSupersetHead}
                supersetNumber={mv.superset_group_id ? supersetNumbers[mv.superset_group_id] : null}
                onToggleSuperset={toggleSuperset}
              />
            );
          });
        })()}
      </div>

      {canEditCore && (
        <Dialog open={addMvOpen} onOpenChange={setAddMvOpen}>
          <DialogTrigger asChild>
            <Button variant="secondary" className="mt-3 w-full rounded-xl" data-testid={`add-movement-to-block-${blk.id}`}>
              <Plus size={14} className="mr-2" /> Add movement
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-h-[92vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="font-heading">Add movement to {blk.title || meta.label}</DialogTitle>
              <DialogDescription>Target sets, reps, and metrics. You can pre-fill from the Library.</DialogDescription>
            </DialogHeader>
            <MovementForm enableLibrary onSave={addMvInBlock} onCancel={() => setAddMvOpen(false)} />
          </DialogContent>
        </Dialog>
      )}

      {!isStrengthLike && (blk.movements || []).length > 0 && (
        <div className="mt-3">
          <BlockTimer
            block={blk}
            program={program}
            dayIdx={dayIdx}
            athleteId={athleteId}
          />
        </div>
      )}

      {!isStrengthLike && userRole === "athlete" && (blk.movements || []).length > 0 && (
        <div className="mt-3">
          <BlockCompletionDialog
            block={blk}
            program={program}
            dayIdx={dayIdx}
            athleteId={athleteId}
          />
        </div>
      )}
    </div>
  );
}
